import {Accordion} from '@dropbox/dig-components/accordion';
import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text, Title} from '@dropbox/dig-components/typography';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AiChatLine,
  AiWriteLine,
  CommentLine,
  PaperDocLine,
} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {DisclaimerType, Review} from 'client';
import {DisclaimerModal} from 'components/disclaimer_modal/disclaimer_modal';
import {
  FeedbackController,
  SortOrder,
  useGetFeedback,
} from 'components/manager_feedback/feedback';
import {FeedbackFilters} from 'components/manager_feedback/feedback_filters';
import {
  FeedbackQuotesController,
  useGetFeedbackQuotes,
  useGetFeedbackSummary,
} from 'components/manager_feedback/feedback_quotes';
import {ReviewGPTController} from 'components/manager_feedback/review_gpt';
import {
  spritesCycleAtom,
  useGetReviewCycles,
} from 'components/manager_feedback/sprites_cycle_selector';
import {TextBubble} from 'components/manager_feedback/text_bubble';
import {useQueryParamState} from 'hooks/use_query_params';
import {useUserPermissions} from 'hooks/use_user_permissions';
import {useTabTimeTracking} from 'hooks/useTabTimeTracking';
import {useAtomValue} from 'jotai';
import React from 'react';
import {DropboxerId} from 'store/features/feedback_chat/types';
import {API_URL, getDropboxService, isSandboxMode} from 'utilities';
import {
  ANALYTICS_EVENTS,
  sendAnalyticsEvent,
  TAB_VISIT_EVENT_MAP,
} from 'utilities/analytics';

import {CycleDropdown} from './cycle_dropdown';
import styles from './manager_feedback.module.css';
import {
  findActiveReviewCycle,
  findAnnualManagerReviewForSelectedCycle,
  findDietManagerReviewForSelectedCycle,
  findReviewForSelectedCycle,
  isDietSpritesSelectedCycle,
  VIEWABLE_REVIEW_STATUSES,
} from './utils';

const usePrefetchTabData = (
  dropboxerId: DropboxerId,
  prefetchAiTabs: boolean
) => {
  useGetFeedback(dropboxerId);
  useGetFeedbackQuotes(dropboxerId, prefetchAiTabs);
  useGetFeedbackSummary(dropboxerId, prefetchAiTabs);
  // Send GA event
  sendAnalyticsEvent(ANALYTICS_EVENTS.MANAGER_ASSISTANT.FETCH_FEEDBACK_DATA, {
    category: 'Manager Assistant',
  });
};

const FEEDBACK_TAB_IDS: readonly string[] = [
  'self_review',
  'manager_review',
  'feedback',
  'review_gpt',
] as const;

const SELF_REVIEW_FIELD_LABELS = {
  question_1: 'Business Impact',
  question_2: 'Strengths',
  question_3: 'Development Areas',
  question_4: 'Career Development (Optional)',
} as const;

const MANAGER_REVIEW_FIELD_LABELS = {
  manager_question_1: 'Business Impact',
  manager_question_2: 'Strengths',
  manager_question_3: 'Development Areas',
  manager_question_4: 'Career Development',
} as const;

// Labels for Diet SPRiTEs review self-review questions
const DIET_SPRITES_FIELD_LABELS = {
  question_2: 'Strengths',
  question_3: 'Development Areas',
} as const;

const hasAnyManagerQuestion = (review: Review) =>
  review.manager_question_1 != null ||
  review.manager_question_2 != null ||
  review.manager_question_3 != null ||
  review.manager_question_4 != null;

const getReviews = async (dropboxerId: DropboxerId) => {
  const reviewResponse =
    await getDropboxService().getReviewsApiV1DropboxerReviewsEmployeeIdGet(
      dropboxerId
    );
  return reviewResponse.reviews;
};

export const useGetReviews = (dropboxerId: DropboxerId) => {
  const {data, status, error, isRefetching, refetch} = useQuery({
    queryKey: ['/dropboxer/reviews', dropboxerId],
    queryFn: () => getReviews(dropboxerId),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  if (status === 'loading') {
    return {status};
  }

  if (status === 'error') {
    return {status, error, isRefetching, refetch};
  }

  return {
    status,
    reviews: data,
    isRefetching,
    refetch,
  };
};

type SelfReviewControllerProps = {
  dropboxerId: DropboxerId;
};

type SingleReviewProps = {
  review: Review;
};

const SingleReview = ({review}: SingleReviewProps) => {
  const selectedCycle = useAtomValue(spritesCycleAtom);
  const employeeName = `${review.preferred_first_name} ${review.preferred_last_name}`;
  const canViewAnswers = VIEWABLE_REVIEW_STATUSES.includes(review.status);

  const reviewFields = isDietSpritesSelectedCycle(selectedCycle)
    ? [
        {
          id: 'question_2',
          label: DIET_SPRITES_FIELD_LABELS.question_2,
          text: review.question_2,
        },
        {
          id: 'question_3',
          label: DIET_SPRITES_FIELD_LABELS.question_3,
          text: review.question_3,
        },
      ]
    : [
        {
          id: 'question_1',
          label: SELF_REVIEW_FIELD_LABELS.question_1,
          text: review.question_1,
        },
        {
          id: 'question_2',
          label: SELF_REVIEW_FIELD_LABELS.question_2,
          text: review.question_2,
        },
        {
          id: 'question_3',
          label: SELF_REVIEW_FIELD_LABELS.question_3,
          text: review.question_3,
        },
        {
          id: 'question_4',
          label: SELF_REVIEW_FIELD_LABELS.question_4,
          text: review.question_4,
        },
      ];

  return (
    <div className={styles['self-review-container']}>
      <Title size="small" weightVariant="emphasized">
        {review.review_cycle} Self-Review
      </Title>

      <div className={styles['self-review-info']}>
        <div style={{marginBottom: '0.5rem'}}>
          <Text variant="paragraph" tagName="p" style={{margin: 0}}>
            Name: {employeeName}
          </Text>
          <Text variant="paragraph" tagName="p" style={{margin: 0}}>
            Status: {review.status}
          </Text>
        </div>
      </div>

      {canViewAnswers ? (
        <div className={styles['self-review-fields']}>
          {reviewFields.map((field) => (
            <div key={field.id} className={styles['self-review-field']}>
              <Text variant="label" tagName="label">
                {field.label}
              </Text>
              <div className={styles['self-review-text-box']}>
                {field.text ? (
                  <TextBubble html={field.text} />
                ) : (
                  <Text variant="paragraph" tagName="p" color="subtle">
                    Not provided
                  </Text>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles['self-review-pending']}>
          <Text variant="paragraph" tagName="p" color="subtle">
            This self-review is currently in progress and not yet available for
            viewing.
          </Text>
        </div>
      )}
    </div>
  );
};

const SelfReviewController = ({dropboxerId}: SelfReviewControllerProps) => {
  const {status, reviews, isRefetching, refetch} = useGetReviews(dropboxerId);
  const selectedCycle = useAtomValue(spritesCycleAtom);

  if (status === 'loading') {
    return (
      <div className={styles['generic-container']}>
        <Spinner size="large" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={styles['generic-container']}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          Error loading self-review data.
        </Text>
        <Button
          variant="primary"
          onClick={() => refetch?.()}
          isLoading={isRefetching}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div className={styles['generic-container']}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          No Self Review for this employee for this cycle
        </Text>
      </div>
    );
  }

  const matchingReview = findReviewForSelectedCycle(
    reviews,
    selectedCycle,
    isSandboxMode()
  );

  if (!matchingReview) {
    return (
      <div className={styles['generic-container']}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          There is no Self Review for this Cycle
        </Text>
      </div>
    );
  }

  return <SingleReview review={matchingReview} />;
};

type ManagerReviewControllerProps = {
  dropboxerId: DropboxerId;
};

/**
 * Format Diet SPRiTEs review cycle title by removing "(Updated)" and
 * changing "Reviews" to "Review" (singular).
 * Example: "2025 Diet SPRiTEs Reviews (Updated)" -> "2025 Diet SPRiTEs Review"
 */
const formatDietSpritesTitle = (reviewCycle: string): string => {
  return reviewCycle
    .replace(' (Updated)', '')
    .replace('Diet SPRiTEs Reviews', 'Diet SPRiTEs Review');
};

/**
 * Accordion component to display Diet SPRiTEs Review content (manager_question_2 and manager_question_3)
 * when a dropboxer has a past review with a review cycle containing
 * "Diet SPRiTEs Review". Expanded by default.
 */
const DietSpritesAccordion = ({review}: {review: Review}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const title = formatDietSpritesTitle(review.review_cycle);

  const reviewFields = [
    {
      id: 'manager_question_2',
      label: DIET_SPRITES_FIELD_LABELS.question_2,
      text: review.manager_question_2,
    },
    {
      id: 'manager_question_3',
      label: DIET_SPRITES_FIELD_LABELS.question_3,
      text: review.manager_question_3,
    },
  ];

  return (
    <Accordion
      variant="standard"
      expandedPanels={isExpanded ? ['diet_sprites'] : []}
    >
      <Accordion.Item itemId="diet_sprites">
        <Accordion.Header
          size="large"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Accordion.HeaderContent withTitle={title} />
        </Accordion.Header>
        <Accordion.Panel>
          <div className={styles['self-review-fields']}>
            {reviewFields.map((field) => (
              <div key={field.id} className={styles['self-review-field']}>
                <Text variant="label" tagName="label">
                  {field.label}
                </Text>
                <div className={styles['self-review-text-box']}>
                  {field.text ? (
                    <TextBubble html={field.text} />
                  ) : (
                    <Text variant="paragraph" tagName="p" color="subtle">
                      Not provided
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

interface AnnualReviewAccordionProps extends SingleReviewProps {
  defaultExpanded?: boolean;
}

/**
 * Accordion component to display Annual SPRiTEs Manager Review content.
 * Expanded by default.
 */
const AnnualReviewAccordion = ({
  review,
  defaultExpanded = true,
}: AnnualReviewAccordionProps) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const title = `${review.review_cycle} Manager Review`;

  // Manager reviews are shown if at least one manager question is answered
  const hasManagerReview = hasAnyManagerQuestion(review);

  const reviewFields = [
    {
      id: 'manager_question_1',
      label: MANAGER_REVIEW_FIELD_LABELS.manager_question_1,
      text: review.manager_question_1,
    },
    {
      id: 'manager_question_2',
      label: MANAGER_REVIEW_FIELD_LABELS.manager_question_2,
      text: review.manager_question_2,
    },
    {
      id: 'manager_question_3',
      label: MANAGER_REVIEW_FIELD_LABELS.manager_question_3,
      text: review.manager_question_3,
    },
    {
      id: 'manager_question_4',
      label: MANAGER_REVIEW_FIELD_LABELS.manager_question_4,
      text: review.manager_question_4,
    },
  ];

  return (
    <Accordion
      variant="standard"
      expandedPanels={isExpanded ? ['annual_review'] : []}
    >
      <Accordion.Item itemId="annual_review">
        <Accordion.Header
          size="large"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Accordion.HeaderContent withTitle={title} />
        </Accordion.Header>
        <Accordion.Panel>
          <div className={styles['self-review-info']}>
            <div style={{marginBottom: '0.5rem'}}>
              <Text variant="paragraph" tagName="p" style={{margin: 0}}>
                Evaluated By: {review.evaluator || 'Unknown'}
              </Text>
            </div>
          </div>

          {hasManagerReview ? (
            <div className={styles['self-review-fields']}>
              {reviewFields.map((field) => (
                <div key={field.id} className={styles['self-review-field']}>
                  <Text variant="label" tagName="label">
                    {field.label}
                  </Text>
                  <div className={styles['self-review-text-box']}>
                    {field.text ? (
                      <TextBubble html={field.text} />
                    ) : (
                      <Text variant="paragraph" tagName="p" color="subtle">
                        Not provided
                      </Text>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles['self-review-pending']}>
              <Text variant="paragraph" tagName="p" color="subtle">
                No manager review is available for this cycle.
              </Text>
            </div>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

const ManagerReviewController = ({
  dropboxerId,
}: ManagerReviewControllerProps) => {
  const {status, reviews, isRefetching, refetch} = useGetReviews(dropboxerId);
  const selectedCycle = useAtomValue(spritesCycleAtom);

  if (status === 'loading') {
    return (
      <div className={styles['generic-container']}>
        <Spinner size="large" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={styles['generic-container']}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          Error loading manager review data.
        </Text>
        <Button
          variant="primary"
          onClick={() => refetch?.()}
          isLoading={isRefetching}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div className={styles['generic-container']}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          No manager review data found for this employee.
        </Text>
      </div>
    );
  }

  const isDietCycle = isDietSpritesSelectedCycle(selectedCycle);
  const isSandbox = isSandboxMode();

  if (isDietCycle) {
    const dietReview = findDietManagerReviewForSelectedCycle(
      reviews,
      selectedCycle,
      isSandbox
    );

    if (!dietReview) {
      return (
        <div className={styles['generic-container']}>
          <Text variant="paragraph" tagName="p" size="xlarge">
            {isSandbox
              ? 'There is no Manager Review for this Cycle'
              : 'There is no Diet SPRiTEs Manager Review for this Cycle'}
          </Text>
        </div>
      );
    }

    return <DietSpritesAccordion review={dietReview} />;
  }

  const matchingReview = findAnnualManagerReviewForSelectedCycle(
    reviews,
    selectedCycle,
    isSandbox
  );

  if (!matchingReview) {
    return (
      <div className={styles['generic-container']}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          There is no Manager Review for this Cycle
        </Text>
      </div>
    );
  }

  return <AnnualReviewAccordion review={matchingReview} />;
};

// ReviewGPT is only available when the selected cycle is active (OPEN or CREATED)
const OPEN_CYCLE_TABS = ['review_gpt'];

const useEmployeeOptOutStatus = (dropboxerId: DropboxerId) => {
  const isSandbox = isSandboxMode();
  const {data, isLoading} = useQuery({
    queryKey: ['/dropboxer/opt-out-status', dropboxerId, isSandbox],
    queryFn: async () => {
      const params = new URLSearchParams({
        is_sandbox: String(isSandbox),
      });
      const response = await fetch(
        `${API_URL}/api/v1/dropboxer/${dropboxerId}/opt-out-status?${params}`,
        {credentials: 'include'}
      );
      if (!response.ok) return {has_any_opt_out: false, is_colorado: false};
      return response.json();
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: !!dropboxerId,
  });
  return {
    hasAnyOptOut: data?.has_any_opt_out ?? false,
    isColorado: data?.is_colorado ?? false,
    isLoading,
  };
};

const useManagerOptOutStatus = () => {
  const isSandbox = isSandboxMode();
  const {data, isLoading} = useQuery({
    queryKey: ['/privacy', isSandbox],
    queryFn: () =>
      getDropboxService().getPrivacyPoliciesApiV1PrivacyGet(isSandbox),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });
  return {
    managerOptedOut: (data?.opt_out_features ?? []).some(
      (f: {opt_out: string; enabled: boolean}) => f.enabled
    ),
    isLoading,
  };
};

const FeedbackTabs = ({dropboxerId}: {dropboxerId: DropboxerId}) => {
  const [selectedTabQueryParam, setSelectedTab] = useQueryParamState('tab');
  const [feedbackSortOrder, setFeedbackSortOrder] =
    React.useState<SortOrder>('desc');

  const [selectedFeedbackTab, setSelectedFeedbackTab] =
    React.useState('all_feedback');

  const {reviewCycles} = useGetReviewCycles();
  const selectedCycle = useAtomValue(spritesCycleAtom);
  const {isColorado: managerInColorado, isLoading: permissionsLoading} =
    useUserPermissions();

  const {
    hasAnyOptOut: reportOptedOut,
    isColorado: reportInColorado,
    isLoading: employeeOptOutLoading,
  } = useEmployeeOptOutStatus(dropboxerId);
  const {managerOptedOut, isLoading: managerOptOutLoading} =
    useManagerOptOutStatus();

  const isComplianceCheckPending =
    permissionsLoading || employeeOptOutLoading || managerOptOutLoading;

  const isBlockedByOptOutOrColorado =
    isComplianceCheckPending ||
    reportOptedOut ||
    managerOptedOut ||
    reportInColorado ||
    managerInColorado;

  const blockedReason = React.useMemo(() => {
    if (isComplianceCheckPending) {
      return 'Checking eligibility for AI tools…';
    }
    if (!isBlockedByOptOutOrColorado) return '';
    if (reportInColorado)
      return 'This employee is based in Colorado. AI tools are not available for Colorado-based employees.';
    if (managerInColorado)
      return 'You are based in Colorado. AI tools are not available for Colorado-based managers.';
    if (reportOptedOut)
      return 'This employee has opted out of AI processing of their data.';
    if (managerOptedOut)
      return 'You have opted out of AI processing. AI tools are not available.';
    return '';
  }, [
    isComplianceCheckPending,
    isBlockedByOptOutOrColorado,
    reportInColorado,
    managerInColorado,
    reportOptedOut,
    managerOptedOut,
  ]);

  const canSeeOpenCycleTabs = React.useMemo(() => {
    if (!reviewCycles || !selectedCycle) return false;
    const activeCycle = findActiveReviewCycle(reviewCycles);
    return activeCycle ? selectedCycle === activeCycle.cycle_name : false;
  }, [reviewCycles, selectedCycle]);

  const availableTabIds = FEEDBACK_TAB_IDS.filter((id) => {
    if (OPEN_CYCLE_TABS.includes(id)) {
      return canSeeOpenCycleTabs;
    }
    return true;
  });

  const selectedTab =
    selectedTabQueryParam && availableTabIds.includes(selectedTabQueryParam)
      ? selectedTabQueryParam
      : availableTabIds[0];

  React.useEffect(() => {
    if (selectedTab !== selectedTabQueryParam) {
      setSelectedTab(selectedTab);
    }
  }, [selectedTab, selectedTabQueryParam, setSelectedTab]);

  usePrefetchTabData(dropboxerId, !isBlockedByOptOutOrColorado);

  // Track time spent on each tab
  useTabTimeTracking(selectedTab);

  const handleTabSelection = (tabId: string) => {
    setSelectedTab(tabId);
    const eventName = TAB_VISIT_EVENT_MAP[tabId];
    if (eventName) {
      sendAnalyticsEvent(eventName, {
        category: 'Manager Assistant',
      });
    }
  };

  return (
    <Tabs
      selectedTab={selectedTab}
      onSelection={handleTabSelection}
      className={styles['content-container']}
    >
      <div className={styles['cycle-dropdown-row']}>
        <CycleDropdown dropboxerId={dropboxerId} />
      </div>
      <Tabs.Group>
        <Tabs.Tab id="self_review">
          <span className={styles['tab-label-with-icon']}>
            <UIIcon
              src={PaperDocLine}
              size="large"
              className={styles['tab-icon']}
              aria-hidden
            />
            Self Review
          </span>
        </Tabs.Tab>
        <Tabs.Tab id="manager_review">
          <span className={styles['tab-label-with-icon']}>
            <UIIcon
              src={PaperDocLine}
              size="large"
              className={styles['tab-icon']}
              aria-hidden
            />
            Manager Review
          </span>
        </Tabs.Tab>
        <Tabs.Tab id="feedback">
          <span className={styles['tab-label-with-icon']}>
            <UIIcon
              src={CommentLine}
              size="large"
              className={styles['tab-icon']}
              aria-hidden
            />
            Feedback
          </span>
        </Tabs.Tab>

        {canSeeOpenCycleTabs && (
          <Tabs.Tab id="review_gpt">
            <span className={styles['tab-label-with-icon']}>
              <UIIcon
                src={AiWriteLine}
                size="large"
                className={styles['tab-icon']}
                aria-hidden
              />
              ReviewGPT
            </span>
          </Tabs.Tab>
        )}
      </Tabs.Group>
      <Tabs.Panel tabId="self_review" className={styles['tabs-panel']}>
        <div className={styles['sync-note']}>
          <Text variant="paragraph" tagName="p" color="subtle" size="small">
            Self review data is synced daily at 5:00am EST
          </Text>
        </div>
        <SelfReviewController dropboxerId={dropboxerId} />
      </Tabs.Panel>
      <Tabs.Panel tabId="manager_review" className={styles['tabs-panel']}>
        <ManagerReviewController dropboxerId={dropboxerId} />
      </Tabs.Panel>
      <Tabs.Panel tabId="feedback" className={styles['tabs-panel']}>
        <Tabs
          selectedTab={selectedFeedbackTab}
          onSelection={setSelectedFeedbackTab}
        >
          <Tabs.Group className={styles['feedback-subtabs']}>
            <Tabs.Tab id="all_feedback">
              <span className={styles['tab-label-with-icon']}>
                <UIIcon
                  src={CommentLine}
                  size="large"
                  className={styles['tab-icon']}
                  aria-hidden
                />
                All Feedback
              </span>
            </Tabs.Tab>
            <Tabs.Tab id="organized_feedback">
              <span className={styles['tab-label-with-icon']}>
                <UIIcon
                  src={AiChatLine}
                  size="large"
                  className={styles['tab-icon']}
                  aria-hidden
                />
                Summarized Feedback
              </span>
            </Tabs.Tab>
          </Tabs.Group>
          <Tabs.Panel tabId="all_feedback">
            <div className={styles['feedback-toolbar']}>
              <Text variant="paragraph" tagName="p" color="subtle" size="small">
                Feedback data is synced daily at 5:00am EST
              </Text>

              <Button
                variant="borderless"
                size="small"
                onClick={() =>
                  setFeedbackSortOrder((prev) =>
                    prev === 'asc' ? 'desc' : 'asc'
                  )
                }
              >
                Date{' '}
                {feedbackSortOrder === 'asc'
                  ? '(oldest to newest) \u2191'
                  : '(newest to oldest) \u2193'}
              </Button>
            </div>

            <FeedbackController
              dropboxerId={dropboxerId}
              sortOrder={feedbackSortOrder}
              grouped={false}
            />
          </Tabs.Panel>

          <Tabs.Panel tabId="organized_feedback">
            {isBlockedByOptOutOrColorado ? (
              <div className={styles['blocked-message']}>
                {isComplianceCheckPending && <Spinner size="large" />}

                <Text variant="paragraph" tagName="p">
                  {blockedReason}
                </Text>
                <UIIcon
                  src={AiChatLine}
                  size="large"
                  className={styles['tab-icon']}
                  aria-hidden
                />
              </div>
            ) : (
              <FeedbackQuotesController dropboxerId={dropboxerId} />
            )}
          </Tabs.Panel>
        </Tabs>
      </Tabs.Panel>
      {canSeeOpenCycleTabs && (
        <Tabs.Panel tabId="review_gpt" className={styles['tabs-panel']}>
          {isBlockedByOptOutOrColorado ? (
            <div className={styles['blocked-message']}>
              {isComplianceCheckPending && <Spinner size="large" />}
              <Text variant="paragraph" tagName="p">
                {blockedReason}
              </Text>
            </div>
          ) : (
            <ReviewGPTController dropboxerId={dropboxerId} />
          )}
        </Tabs.Panel>
      )}
    </Tabs>
  );
};

export const ManagerFeedbackAssistant = () => {
  const [selectedDropboxerId, setSelectedDropboxerId] =
    useQueryParamState('eid');

  return (
    <div className={styles['page']}>
      <DisclaimerModal disclaimerType={DisclaimerType.MANAGER_ASSISTANT} />
      <FeedbackFilters
        selectedDropboxerId={selectedDropboxerId}
        onSelectDropboxer={setSelectedDropboxerId}
      />
      {selectedDropboxerId ? (
        <FeedbackTabs dropboxerId={selectedDropboxerId} />
      ) : (
        <Text
          variant="paragraph"
          tagName="p"
          className={styles['content-container']}
        >
          Select a Dropboxer to view their feedback
        </Text>
      )}
    </div>
  );
};
